import { useEffect, useRef, type ReactNode } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { AttendancePoint } from '../lib/attendance-map';
import { boundsOf } from '../lib/attendance-map';

/**
 * Peta untuk MELIHAT tempat absen terjadi — hanya baca, tidak dapat diklik.
 *
 * Diadaptasi dari `PetaTracking` aplikasi absensi-kurir Emguard, dengan satu hal yang sengaja
 * TIDAK dibawa: garis jejak. Aplikasi itu punya tabel ping posisi berkala, jadi garisnya adalah
 * data. Workforce hanya punya koordinat yang menempel pada event absen; garis lurus di antara
 * dua titik semacam itu adalah kesimpulan yang tidak pernah diukur.
 *
 * Seperti `MapPicker`, ini memakai Leaflet polos — bukan `react-leaflet` — dan menggambar
 * LINGKARAN, bukan pin. Penanda bawaan Leaflet memuat berkas gambar yang jalurnya sering rusak
 * setelah bundling; lingkaran digambar Leaflet sendiri.
 */

/** Hijau untuk masuk, merah untuk pulang — sama seperti titik awal/akhir di aplikasi lama. */
const WARNA: Readonly<Record<AttendancePoint['eventType'], string>> = {
  CHECK_IN: '#05603a',
  CHECK_OUT: '#b42318',
};

export function EventMap({
  points,
  timeLabel,
}: {
  readonly points: readonly AttendancePoint[];
  /** Pemformat waktu milik pemanggil — zona waktu kebijakan tidak diketahui komponen ini. */
  readonly timeLabel: (occurredAtServer: string) => string;
}): ReactNode {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  // Disimpan di ref agar peta tidak dibuat ulang hanya karena pemformat berubah identitasnya.
  const timeLabelRef = useRef(timeLabel);
  timeLabelRef.current = timeLabel;

  useEffect(() => {
    const container = containerRef.current;
    if (container === null || mapRef.current !== null) return;

    // Jakarta sebagai tampilan awal bila belum ada titik — bukan (0,0), yang berada di laut lepas
    // Ghana dan membuat penyelia mengira petanya rusak.
    const map = L.map(container, { scrollWheelZoom: false }).setView([-6.2, 106.816666], 11);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
    // Daftar dependensi sengaja KOSONG: efek ini hanya membangun peta sekali. Titik-titiknya
    // digambar efek di bawah, yang mengganti isi satu lapisan tanpa membongkar peta — membongkar
    // peta akan mengembalikan zoom dan geseran yang baru saja diatur penyelia.
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (map === null || layer === null) return;

    layer.clearLayers();
    for (const point of points) {
      const posisi: L.LatLngExpression = [point.latitude, point.longitude];
      const warna = WARNA[point.eventType];

      /**
       * Lingkaran akurasi digambar LEBIH DULU agar berada di bawah titiknya.
       *
       * Akurasi yang tidak dilaporkan tidak digambar sama sekali — lingkaran seluas nol akan
       * terbaca sebagai "tepat di sini", klaim ketepatan yang perangkatnya tidak pernah buat.
       */
      if (point.accuracyMeters !== null && point.accuracyMeters > 0) {
        L.circle(posisi, {
          radius: point.accuracyMeters,
          color: warna,
          weight: 1,
          opacity: 0.35,
          fillColor: warna,
          fillOpacity: 0.08,
          interactive: false,
        }).addTo(layer);
      }

      const geofence =
        point.insideGeofence === null
          ? 'Tidak diperiksa terhadap radius lokasi kerja'
          : point.insideGeofence
            ? 'Di dalam radius'
            : 'Di luar radius';
      const akurasi =
        point.accuracyMeters === null
          ? 'akurasi tidak dilaporkan'
          : `± ${Math.round(point.accuracyMeters)} m`;

      // `circleMarker` — radius dalam PIKSEL, jadi titiknya tetap terlihat pada zoom berapa pun.
      // `circle` di atas memakai radius METER, sehingga ia mengecil saat peta di-zoom keluar,
      // persis seperti yang seharusnya untuk ketidakpastian di dunia nyata.
      L.circleMarker(posisi, {
        radius: 7,
        color: '#ffffff',
        weight: 2,
        fillColor: warna,
        fillOpacity: 1,
      })
        .bindPopup(
          `<b>${point.eventType === 'CHECK_IN' ? 'Absen masuk' : 'Absen pulang'} ${escapeHtml(
            timeLabelRef.current(point.occurredAtServer),
          )}</b><br>${geofence}<br>${akurasi}`,
        )
        .addTo(layer);
    }

    const kotak = boundsOf(points);
    if (kotak === null) return;
    if (points.length === 1) {
      // `fitBounds` pada kotak setinggi nol melompat ke zoom maksimum — sebuah jalan yang terlihat
      // seperti gang buntu, tanpa konteks apa pun di sekitarnya.
      map.setView([kotak.south, kotak.west], 16);
    } else {
      map.fitBounds(
        [
          [kotak.south, kotak.west],
          [kotak.north, kotak.east],
        ],
        { padding: [40, 40], maxZoom: 17 },
      );
    }
  }, [points]);

  return (
    <div
      ref={containerRef}
      className="event-map"
      // Peta adalah gambar interaktif; pembaca layar tidak dapat memakainya. Daftar event di
      // bawahnya memuat waktu, verifikasi, dan jarak untuk setiap titik — itulah jalur setara,
      // dinyatakan di sini agar tidak dikira terlewat.
      role="application"
      aria-label="Peta lokasi absen. Rincian setiap event tersedia sebagai teks pada daftar di bawah peta."
    />
  );
}

/** Isi popup dirakit sebagai HTML, jadi teks apa pun dari server harus dilewatkan ini dulu. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
